import { useEffect, useRef, useCallback } from 'react';
import DraftMessageCacheService from '../../../../services/draftMessageCacheService';
import { DraftMessage } from '../../../../mobx/interfaces/comment';
import { CommentFilesInterface } from '../../../../interfaces/TaskInterface';
import { DRAFT_MESSAGE_TYPE } from '../../../../global/constants';

const DRAFT_SAVE_DELAY = 100;

export const useCommentDraft = (
  taskId: string | undefined,
  valueContent: string,
  uploadedFiles: CommentFilesInterface[],
  setValueContent: (value: string) => void,
  setUploadedFilesLocal: (files: CommentFilesInterface[]) => void
) => {
  const saveTimer = useRef<number | null>(null);

  // Load draft on mount / when taskId changes
  useEffect(() => {
    if (!taskId) return;
    let mounted = true;
    (async () => {
      try {
        const draftRaw = await DraftMessageCacheService.getInstance()?.getCurrentDraftMessage(taskId);
        const draft: DraftMessage | null = (draftRaw as DraftMessage) ?? null;
        if (!mounted) return;
        if (draft) {
          setValueContent(draft.message || '');
          setUploadedFilesLocal(draft.file || []);
        } else {
          setValueContent('');
          setUploadedFilesLocal([]);
        }
      } catch (error) {
        // swallow — draft loading is non-critical
      }
    })();

    return () => {
      mounted = false;
    };
  }, [taskId, setValueContent, setUploadedFilesLocal]);

  // Persist draft (debounced)
  useEffect(() => {
    if (!taskId) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      try {
        const messageObject: DraftMessage = {
          id: taskId,
          type: DRAFT_MESSAGE_TYPE.COMMENT,
          message: valueContent,
          file: uploadedFiles || []
        };
        await DraftMessageCacheService.getInstance()?.addBulk([messageObject]);
        if ((valueContent === '' || valueContent === '<p></p>') && (!uploadedFiles || uploadedFiles.length === 0)) {
          await DraftMessageCacheService.getInstance()?.removeBulk([taskId]);
        }
      } catch (error) {
        // ignore persistence errors
      }
    }, DRAFT_SAVE_DELAY);

    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [taskId, valueContent, uploadedFiles]);

  const clearDraft = useCallback(async () => {
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    if (taskId) {
      try {
        await DraftMessageCacheService.getInstance()?.removeBulk([taskId]);
      } catch (error) {
        // ignore errors
      }
    }
  }, [taskId]);

  return { clearDraft };
};
